/**
 * imageToFormula.ts
 * 画像からグラフ曲線を検出し、数式に近似するユーティリティ
 *
 * 改善点:
 * - 色ベースの複数曲線分離（青・緑・赤など色で分ける）
 * - 高次多項式（最大8次）フィット
 * - より多くの候補関数（sin/cos/exp/log/sqrt）
 * - 軸線除去の精度向上
 * - サンプリング密度の大幅向上
 */

export interface ExtractedCurve {
  points: { x: number; y: number }[];
  formula: string;
  r2: number;
  type: 'polynomial' | 'sinusoidal' | 'exponential' | 'logarithmic' | 'raw';
  color?: string;
}

export interface ImageAnalysisResult {
  curves: ExtractedCurve[];
  width: number;
  height: number;
  previewDataUrl: string;
  message: string;
}

// ─── 背景推定 ────────────────────────────────────────────────────

function estimateBackground(imageData: ImageData): 'light' | 'dark' {
  const { data, width, height } = imageData;
  let light = 0, dark = 0;
  const total = width * height;
  const step = Math.max(1, Math.floor(total / 3000));
  for (let i = 0; i < total; i += step) {
    const idx = i * 4;
    const br = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
    br > 160 ? light++ : dark++;
  }
  return light >= dark ? 'light' : 'dark';
}

// ─── グラフ領域の自動検出 ────────────────────────────────────────

function detectPlotRegion(
  imageData: ImageData,
  width: number,
  height: number,
  bg: 'light' | 'dark',
): { x0: number; y0: number; x1: number; y1: number } {
  const { data } = imageData;
  const threshold = bg === 'light' ? 100 : 155;

  const colDensity = new Float32Array(width);
  const rowDensity = new Float32Array(height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const br = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
      const isLine = bg === 'light' ? br < threshold : br > threshold;
      if (isLine) { colDensity[x]++; rowDensity[y]++; }
    }
  }

  const marginX = Math.max(Math.floor(width * 0.06), 4);
  const marginY = Math.max(Math.floor(height * 0.06), 4);

  const maxCol = Math.max(...Array.from(colDensity));
  const maxRow = Math.max(...Array.from(rowDensity));
  const axisT = 0.55;

  let x0 = marginX, x1 = width - marginX;
  let y0 = marginY, y1 = height - marginY;

  // 左軸を検出
  for (let x = marginX; x < width * 0.45; x++) {
    if (colDensity[x] > maxCol * axisT) { x0 = Math.min(x + 3, width - marginX); break; }
  }
  // 下軸を検出
  for (let y = height - marginY; y > height * 0.55; y--) {
    if (rowDensity[y] > maxRow * axisT) { y1 = Math.max(y - 3, marginY); break; }
  }
  // 上端マージン
  for (let y = marginY; y < height * 0.35; y++) {
    if (rowDensity[y] > maxRow * axisT) { y0 = Math.min(y + 3, height - marginY); break; }
  }

  x0 = Math.max(x0, Math.floor(width * 0.04));
  x1 = Math.min(x1, Math.floor(width * 0.98));
  y0 = Math.max(y0, Math.floor(height * 0.04));
  y1 = Math.min(y1, Math.floor(height * 0.98));

  return { x0, y0, x1, y1 };
}

// ─── 色ベースの曲線グループ分け ──────────────────────────────────

interface ColoredPixel {
  x: number;
  y: number;
  hue: number;
  sat: number;
}

function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === rn) h = ((gn - bn) / d + 6) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

// ─── 軸線・グリッド線の除去 ──────────────────────────────────────

function removeAxisLines(
  columnMap: Map<number, ColoredPixel[]>,
  width: number,
  height: number,
): Map<number, ColoredPixel[]> {
  const cleaned = new Map<number, ColoredPixel[]>();

  // 水平線検出: 同じy座標が多くの列に存在
  const yCount = new Map<number, number>();
  for (const pixels of columnMap.values()) {
    for (const p of pixels) {
      yCount.set(p.y, (yCount.get(p.y) ?? 0) + 1);
    }
  }
  const hLineThreshold = width * 0.35;
  const hLines = new Set<number>();
  for (const [y, count] of yCount) {
    if (count > hLineThreshold) hLines.add(y);
  }

  // 垂直線検出: 同じx列に多くの連続ピクセル
  const vLineThreshold = height * 0.35;

  for (const [x, pixels] of columnMap) {
    const ys = pixels.map((p) => p.y).sort((a, b) => a - b);
    let maxRun = 0, run = 1;
    for (let i = 1; i < ys.length; i++) {
      run = ys[i] - ys[i - 1] <= 3 ? run + 1 : 1;
      maxRun = Math.max(maxRun, run);
    }
    if (maxRun > vLineThreshold) continue;

    const filtered = pixels.filter((p) => !hLines.has(p.y));
    if (filtered.length > 0) cleaned.set(x, filtered);
  }

  return cleaned;
}

// ─── ピクセル抽出（色情報付き） ──────────────────────────────────

function extractColoredPixels(
  imageData: ImageData,
  width: number,
  height: number,
  bg: 'light' | 'dark',
  region: { x0: number; y0: number; x1: number; y1: number },
): Map<number, ColoredPixel[]> {
  const { data } = imageData;
  const columnMap = new Map<number, ColoredPixel[]>();

  for (let y = region.y0; y < region.y1; y++) {
    for (let x = region.x0; x < region.x1; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx], g = data[idx + 1], b = data[idx + 2], a = data[idx + 3];
      if (a < 50) continue;

      const br = r * 0.299 + g * 0.587 + b * 0.114;
      const { h, s, v } = rgbToHsv(r, g, b);

      // 背景色を除外
      const isBg = bg === 'light' ? br > 200 : br < 55;
      if (isBg) continue;

      // グレー（軸・グリッド）を除外: 彩度が低い
      // ただし黒い曲線（彩度0）は残す
      const isColoredLine = s > 0.25 || (bg === 'light' && br < 80) || (bg === 'dark' && br > 175);
      if (!isColoredLine) continue;

      if (!columnMap.has(x)) columnMap.set(x, []);
      columnMap.get(x)!.push({ x, y, hue: h, sat: s });
    }
  }

  return columnMap;
}

// ─── 色でグループ分け ────────────────────────────────────────────

interface CurveGroup {
  pixels: { x: number; y: number }[];
  hue: number;
  label: string;
}

function groupByColor(
  columnMap: Map<number, ColoredPixel[]>,
  region: { x0: number; y0: number; x1: number; y1: number },
  xRange: [number, number],
  yRange: [number, number],
): CurveGroup[] {
  // 全ピクセルの色相をヒストグラム化
  const hueHist = new Float32Array(36); // 10度刻み
  let totalColored = 0;
  for (const pixels of columnMap.values()) {
    for (const p of pixels) {
      if (p.sat > 0.3) {
        hueHist[Math.floor(p.hue / 10) % 36]++;
        totalColored++;
      }
    }
  }

  // 支配的な色相グループを検出
  const dominantHues: number[] = [];
  if (totalColored > 20) {
    const maxH = Math.max(...Array.from(hueHist));
    for (let i = 0; i < 36; i++) {
      if (hueHist[i] > maxH * 0.15) {
        // 近い色相をマージ
        const hue = i * 10 + 5;
        const isMerged = dominantHues.some((h) => Math.abs(h - hue) < 30 || Math.abs(h - hue) > 330);
        if (!isMerged) dominantHues.push(hue);
      }
    }
  }

  const regionW = region.x1 - region.x0;
  const regionH = region.y1 - region.y0;

  const toWorld = (px: number, py: number) => ({
    x: xRange[0] + ((px - region.x0) / regionW) * (xRange[1] - xRange[0]),
    y: yRange[1] - ((py - region.y0) / regionH) * (yRange[1] - yRange[0]),
  });

  if (dominantHues.length <= 1) {
    // 単色 or 黒線: 全ピクセルを1グループ
    const allPts: { x: number; y: number }[] = [];
    const sortedX = Array.from(columnMap.keys()).sort((a, b) => a - b);
    for (const px of sortedX) {
      const pixels = columnMap.get(px)!;
      const clusters = clusterY(pixels.map((p) => p.y));
      for (const cluster of clusters) {
        const medY = cluster[Math.floor(cluster.length / 2)];
        allPts.push(toWorld(px, medY));
      }
    }
    return allPts.length >= 5 ? [{ pixels: allPts, hue: dominantHues[0] ?? 0, label: '曲線1' }] : [];
  }

  // 複数色: 色相でグループ分け
  const groups: CurveGroup[] = dominantHues.map((hue, i) => ({
    pixels: [],
    hue,
    label: `曲線${i + 1}`,
  }));

  const sortedX = Array.from(columnMap.keys()).sort((a, b) => a - b);
  for (const px of sortedX) {
    const pixels = columnMap.get(px)!;
    // 色相でグループに振り分け
    const grouped = new Map<number, number[]>();
    for (const p of pixels) {
      let bestGroup = -1;
      let bestDist = Infinity;
      for (let gi = 0; gi < dominantHues.length; gi++) {
        const hDiff = Math.min(
          Math.abs(p.hue - dominantHues[gi]),
          360 - Math.abs(p.hue - dominantHues[gi])
        );
        if (hDiff < bestDist) { bestDist = hDiff; bestGroup = gi; }
      }
      if (bestGroup >= 0 && bestDist < 40) {
        if (!grouped.has(bestGroup)) grouped.set(bestGroup, []);
        grouped.get(bestGroup)!.push(p.y);
      }
    }
    for (const [gi, ys] of grouped) {
      const clusters = clusterY(ys);
      for (const cluster of clusters) {
        const medY = cluster[Math.floor(cluster.length / 2)];
        groups[gi].pixels.push(toWorld(px, medY));
      }
    }
  }

  return groups.filter((g) => g.pixels.length >= 5);
}

function clusterY(ys: number[]): number[][] {
  if (ys.length === 0) return [];
  const sorted = [...ys].sort((a, b) => a - b);
  const clusters: number[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] <= 5) {
      clusters[clusters.length - 1].push(sorted[i]);
    } else {
      clusters.push([sorted[i]]);
    }
  }
  return clusters;
}

// ─── 線形代数ユーティリティ ──────────────────────────────────────

function transpose(A: number[][]): number[][] {
  return A[0].map((_, j) => A.map((row) => row[j]));
}
function matMul(A: number[][], B: number[][]): number[][] {
  return A.map((row) => B[0].map((_, j) => row.reduce((s, v, k) => s + v * B[k][j], 0)));
}
function matVecMul(A: number[][], b: number[]): number[] {
  return A.map((row) => row.reduce((s, v, k) => s + v * b[k], 0));
}
function gaussianElimination(A: number[][], b: number[]): number[] | null {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row;
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];
    if (Math.abs(M[col][col]) < 1e-12) return null;
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / M[col][col];
      for (let k = col; k <= n; k++) M[row][k] -= factor * M[col][k];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = M[i][n];
    for (let j = i + 1; j < n; j++) x[i] -= M[i][j] * x[j];
    x[i] /= M[i][i];
  }
  return x;
}

// ─── フィッティング関数群 ────────────────────────────────────────

function calcR2(points: { x: number; y: number }[], predict: (x: number) => number): number {
  const ys = points.map((p) => p.y);
  const yMean = ys.reduce((s, v) => s + v, 0) / ys.length;
  const ssTot = ys.reduce((s, v) => s + (v - yMean) ** 2, 0);
  const ssRes = points.reduce((s, { x, y }) => s + (y - predict(x)) ** 2, 0);
  return ssTot === 0 ? 1 : Math.max(0, 1 - ssRes / ssTot);
}

function fitPolynomial(
  points: { x: number; y: number }[],
  degree: number,
): { coeffs: number[]; r2: number } {
  const n = points.length;
  if (n < degree + 1) return { coeffs: [], r2: 0 };
  const A = points.map(({ x }) => Array.from({ length: degree + 1 }, (_, k) => Math.pow(x, k)));
  const b = points.map(({ y }) => y);
  const AT = transpose(A);
  const coeffs = gaussianElimination(matMul(AT, A), matVecMul(AT, b));
  if (!coeffs) return { coeffs: [], r2: 0 };
  const r2 = calcR2(points, (x) => coeffs.reduce((s, c, k) => s + c * Math.pow(x, k), 0));
  return { coeffs, r2 };
}

function fitSinusoidal(points: { x: number; y: number }[]): { A: number; B: number; C: number; D: number; r2: number } {
  if (points.length < 6) return { A: 1, B: 1, C: 0, D: 0, r2: 0 };
  const ys = points.map((p) => p.y);
  const D = (Math.max(...ys) + Math.min(...ys)) / 2;
  const A = (Math.max(...ys) - Math.min(...ys)) / 2;
  if (A < 1e-6) return { A, B: 1, C: 0, D, r2: 0 };
  const centered = ys.map((y) => y - D);
  let crossings = 0;
  for (let i = 1; i < centered.length; i++) {
    if (centered[i - 1] * centered[i] < 0) crossings++;
  }
  const xSpan = points[points.length - 1].x - points[0].x;
  const B = crossings > 1 ? (Math.PI * crossings) / xSpan : (2 * Math.PI) / Math.max(xSpan, 0.1);
  const r2 = calcR2(points, (x) => A * Math.sin(B * x) + D);
  return { A, B, C: 0, D, r2 };
}

function fitExponential(points: { x: number; y: number }[]): { a: number; b: number; r2: number } {
  const validPts = points.filter((p) => p.y > 0);
  if (validPts.length < 4) return { a: 1, b: 0, r2: 0 };
  const logPts = validPts.map((p) => ({ x: p.x, y: Math.log(p.y) }));
  const { coeffs } = fitPolynomial(logPts, 1);
  if (!coeffs.length) return { a: 1, b: 0, r2: 0 };
  const a = Math.exp(coeffs[0]);
  const b = coeffs[1];
  const r2 = calcR2(points, (x) => a * Math.exp(b * x));
  return { a, b, r2 };
}

function fitLogarithmic(points: { x: number; y: number }[]): { a: number; b: number; r2: number } {
  const validPts = points.filter((p) => p.x > 0);
  if (validPts.length < 4) return { a: 1, b: 0, r2: 0 };
  const logPts = validPts.map((p) => ({ x: Math.log(p.x), y: p.y }));
  const { coeffs } = fitPolynomial(logPts, 1);
  if (!coeffs.length) return { a: 1, b: 0, r2: 0 };
  const r2 = calcR2(validPts, (x) => coeffs[1] * Math.log(x) + coeffs[0]);
  return { a: coeffs[1], b: coeffs[0], r2 };
}

// ─── 数式文字列生成 ──────────────────────────────────────────────

function round(v: number, digits = 4): number {
  return Math.round(v * 10 ** digits) / 10 ** digits;
}

function coeffsToFormula(coeffs: number[]): string {
  const terms: string[] = [];
  for (let k = coeffs.length - 1; k >= 0; k--) {
    const c = round(coeffs[k]);
    if (Math.abs(c) < 1e-5) continue;
    const cStr = Math.abs(c) === 1 && k > 0 ? (c < 0 ? '-' : '') : `${c}`;
    if (k === 0) terms.push(`${c}`);
    else if (k === 1) terms.push(Math.abs(c) === 1 ? (c < 0 ? '-x' : 'x') : `${c}*x`);
    else terms.push(Math.abs(c) === 1 ? (c < 0 ? `-x^${k}` : `x^${k}`) : `${c}*x^${k}`);
    void cStr;
  }
  if (!terms.length) return '0';
  return terms.join(' + ').replace(/\+ -/g, '- ');
}

// ─── 1曲線のフィッティング ───────────────────────────────────────

function fitCurve(points: { x: number; y: number }[]): ExtractedCurve {
  // 均等サンプリング（最大500点）
  const maxPts = 500;
  const step = Math.max(1, Math.floor(points.length / maxPts));
  const sampled = points.filter((_, i) => i % step === 0);

  // x方向でソート
  sampled.sort((a, b) => a.x - b.x);

  const candidates: { type: ExtractedCurve['type']; r2: number; formula: string }[] = [];

  // 多項式フィット（1〜8次）
  let bestPoly = { coeffs: [] as number[], r2: 0, deg: 1 };
  for (const deg of [1, 2, 3, 4, 5, 6, 7, 8]) {
    if (sampled.length < deg + 2) break;
    const { coeffs, r2 } = fitPolynomial(sampled, deg);
    if (r2 > bestPoly.r2 && coeffs.length > 0) {
      // 過学習チェック: 高次でも係数が小さければ採用
      const highCoeffMag = coeffs.slice(deg - 1).every((c) => Math.abs(c) < 1e4);
      if (highCoeffMag) bestPoly = { coeffs, r2, deg };
    }
  }
  if (bestPoly.coeffs.length > 0) {
    candidates.push({ type: 'polynomial', r2: bestPoly.r2, formula: coeffsToFormula(bestPoly.coeffs) });
  }

  // 三角関数フィット
  const sinFit = fitSinusoidal(sampled);
  if (sinFit.r2 > 0.3) {
    const { A, B, D } = sinFit;
    let formula = `${round(A)}*sin(${round(B)}*x)`;
    if (Math.abs(D) > 1e-4) formula += D > 0 ? ` + ${round(D)}` : ` - ${Math.abs(round(D))}`;
    candidates.push({ type: 'sinusoidal', r2: sinFit.r2, formula });
  }

  // 指数関数フィット
  const expFit = fitExponential(sampled);
  if (expFit.r2 > 0.3) {
    candidates.push({
      type: 'exponential',
      r2: expFit.r2,
      formula: `${round(expFit.a)}*exp(${round(expFit.b)}*x)`,
    });
  }

  // 対数関数フィット
  const logFit = fitLogarithmic(sampled);
  if (logFit.r2 > 0.3) {
    candidates.push({
      type: 'logarithmic',
      r2: logFit.r2,
      formula: `${round(logFit.a)}*ln(x) + ${round(logFit.b)}`,
    });
  }

  // 最良候補を選択
  candidates.sort((a, b) => b.r2 - a.r2);
  const best = candidates[0] ?? { type: 'raw' as const, r2: 0, formula: '0' };

  return { type: best.type, formula: best.formula, r2: best.r2, points: sampled };
}

// ─── プレビュー画像生成 ──────────────────────────────────────────

function generatePreviewDataUrl(
  imageData: ImageData,
  width: number,
  height: number,
  groups: CurveGroup[],
  region: { x0: number; y0: number; x1: number; y1: number },
  xRange: [number, number],
  yRange: [number, number],
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.putImageData(imageData, 0, 0);

  // グラフ領域ハイライト
  ctx.strokeStyle = 'rgba(0,217,255,0.6)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);
  ctx.strokeRect(region.x0, region.y0, region.x1 - region.x0, region.y1 - region.y0);
  ctx.setLineDash([]);

  const colors = ['rgba(0,217,255,0.85)', 'rgba(255,107,53,0.85)', 'rgba(255,215,0,0.85)', 'rgba(127,255,0,0.85)'];
  const regionW = region.x1 - region.x0;
  const regionH = region.y1 - region.y0;

  for (let gi = 0; gi < groups.length; gi++) {
    ctx.fillStyle = colors[gi % colors.length];
    for (const { x, y } of groups[gi].pixels) {
      const px = region.x0 + ((x - xRange[0]) / (xRange[1] - xRange[0])) * regionW;
      const py = region.y0 + ((yRange[1] - y) / (yRange[1] - yRange[0])) * regionH;
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  return canvas.toDataURL('image/png');
}

// ─── メイン解析関数 ──────────────────────────────────────────────

export async function analyzeImageToFormula(
  file: File,
  xRange: [number, number] = [-10, 10],
  yRange: [number, number] = [-10, 10],
): Promise<ImageAnalysisResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        try {
          // 高解像度で解析（最大1200px）
          const maxSize = 1200;
          const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, w, h);
          const imageData = ctx.getImageData(0, 0, w, h);

          // 1. 背景推定
          const bg = estimateBackground(imageData);

          // 2. グラフ領域検出
          const region = detectPlotRegion(imageData, w, h, bg);

          // 3. 色付きピクセル抽出
          const rawColumnMap = extractColoredPixels(imageData, w, h, bg, region);

          if (rawColumnMap.size < 8) {
            resolve({
              curves: [],
              width: w,
              height: h,
              previewDataUrl: dataUrl,
              message: 'グラフの線を検出できませんでした。コントラストの高い画像を使用してください。',
            });
            return;
          }

          // 4. 軸線・グリッド線を除去
          const cleanedMap = removeAxisLines(rawColumnMap, w, h);

          if (cleanedMap.size < 5) {
            resolve({
              curves: [],
              width: w,
              height: h,
              previewDataUrl: dataUrl,
              message: 'グラフ曲線を検出できませんでした。',
            });
            return;
          }

          // 5. 色ベースで曲線グループ分け
          const groups = groupByColor(cleanedMap, region, xRange, yRange);

          if (groups.length === 0) {
            resolve({
              curves: [],
              width: w,
              height: h,
              previewDataUrl: dataUrl,
              message: '曲線を検出できませんでした。',
            });
            return;
          }

          // 6. 各グループをフィッティング
          const curves: ExtractedCurve[] = [];
          for (const group of groups) {
            if (group.pixels.length < 5) continue;
            const fitted = fitCurve(group.pixels);
            curves.push({ ...fitted, color: group.label });
          }

          curves.sort((a, b) => b.r2 - a.r2);

          // 7. プレビュー生成
          const previewDataUrl = generatePreviewDataUrl(imageData, w, h, groups, region, xRange, yRange);

          const bestR2 = curves[0]?.r2 ?? 0;
          const message =
            bestR2 >= 0.90
              ? `高精度でフィット完了（R² = ${round(bestR2, 3)}）`
              : bestR2 >= 0.65
              ? `フィット完了（R² = ${round(bestR2, 3)}）`
              : `フィット精度が低いです（R² = ${round(bestR2, 3)}）。グラフ範囲を調整してみてください。`;

          resolve({ curves, width: w, height: h, previewDataUrl, message });
        } catch (err) {
          reject(new Error('画像解析中にエラーが発生しました: ' + String(err)));
        }
      };
      img.onerror = () => reject(new Error('画像の読み込みに失敗しました。'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました。'));
    reader.readAsDataURL(file);
  });
}
